<template>
	<div class="secretsManagerRoot">
		<div class="pageHeading">Secrets Manager</div>
		<div class="tabRow" role="tablist">
			<router-link :to="{ name: 'clientSecrets' }" class="tabLink" :class="{ activeTab: !isAgentsView }" role="tab" :aria-selected="String(!isAgentsView)">Secrets</router-link>
			<router-link :to="{ name: 'clientSecretsAgents' }" class="tabLink" :class="{ activeTab: isAgentsView }" role="tab" :aria-selected="String(isAgentsView)">Agent Authorization</router-link>
		</div>

		<div v-if="error" class="errorBox">{{error}}</div>

		<!-- Agent Authorization view -->
		<template v-if="isAgentsView">
			<div class="card">
				<div v-if="loadingAgents" class="loadingRow"><ScaleLoader /> Loading agents…</div>
				<template v-else>
					<p class="hintText">No agent is authorized for secrets synchronization by default. An authorized agent receives the secrets whose scope includes it. Slide a control below to authorize or de-authorize an agent. De-authorizing an agent stops synchronization but does not clear the secrets the agent already has.</p>
					<p v-if="!agents.length" class="emptyMessage">No agents are configured.</p>
					<div v-for="agent in agents" :key="agent.AgentId" class="agentAuthRow">
						<div class="agentAuthInfo">
							<span class="statusDot" :class="agent.IsConnected ? 'dotGreen' : 'dotGray'" :title="agent.IsConnected ? 'connected' : 'disconnected'"></span>
							<span class="agentAuthName">{{agent.AgentName}}</span>
							<span class="agentAuthState" :class="{ stateAuthorized: agent.IsAuthorized }">{{agent.IsAuthorized ? 'authorized' : 'not authorized'}}</span>
							<span v-if="agent.Environments && agent.Environments.length" class="mutedText">{{agent.Environments.join(', ')}}</span>
							<span v-if="!agent.IsConnected" class="mutedText">(disconnected)</span>
						</div>
						<slide-button :key="'slideBtn_agent_' + agent.AgentId + '_' + agentSlideBtnCounters[agent.AgentId]"
									  :auto-width="false"
									  :circle="true"
									  :noanimate="true"
									  :width="300"
									  :height="60"
									  :text="agent.IsAuthorized ? 'slide to de-authorize' : 'slide to authorize'"
									  :success-text="agent.IsAuthorized ? 'de-authorizing' : 'authorizing'"
									  @completed="onAgentAuthCommanded(agent)"
									  :requireReleaseToComplete="true" />
					</div>
				</template>
			</div>
		</template>

		<!-- Secrets list view -->
		<template v-else>
			<div class="card toolbarCard">
				<input type="text" class="filterInput" placeholder="Filter by name or description…" v-model="filterText" spellcheck="false" aria-label="Filter secrets by name or description" />
				<button ref="addSecretBtn" type="button" class="gradientButton green" @click="openNewSecretDialog">Add Secret</button>
			</div>
			<div class="card">
				<div v-if="loadingSecrets" class="loadingRow"><ScaleLoader /> Loading secrets…</div>
				<template v-else>
					<div v-if="secrets.length" class="listMeta">{{filterText ? filteredSecrets.length + ' of ' + secrets.length : String(secrets.length)}} secret{{secrets.length === 1 ? '' : 's'}}</div>
					<div v-if="filteredSecrets.length" class="listHeader" aria-hidden="true">
						<div>Secret</div>
						<div>Sync</div>
						<div>Last edit</div>
					</div>
					<div v-for="secret in filteredSecrets" :key="secret.Key" class="secretRow">
						<div class="secretMain">
							<div class="secretTitleRow">
								<button type="button" class="secretNameBtn" :data-secret-key="secret.Key" @click="openSecretDialog(secret)" :title="'Edit secret \'' + secret.Key + '\''">{{secret.Key}}</button>
								<span class="scopeBadge" :class="'scope' + secret.ScopeKind" :title="scopeTooltip(secret)">{{scopeLabel(secret)}}</span>
								<span v-if="secret.WriteOnly" class="writeOnlyBadge" :title="writeOnlyTooltip">write-only</span>
							</div>
							<div v-if="secret.Description" class="secretDescription">{{secret.Description}}</div>
							<div class="secretValueRow">
								<span v-if="!secret.HasValue" class="blankValue" title="The value is null.">&lt;null&gt;</span>
								<span v-else-if="secret.IsBlank" class="blankValue" title="The value is an empty string.">&lt;blank&gt;</span>
								<template v-else>
									<span v-if="secret.Fingerprint" class="fingerprint" :title="fingerprintTooltip" :aria-label="'Value fingerprint ' + secret.Fingerprint">{{secret.Fingerprint}}</span>
									<span v-else class="blankValue" :title="noFingerprintTooltip">value set, no fingerprint yet</span>
									<template v-if="!secret.WriteOnly">
										<button type="button" class="copyBtn" @click="toggleReveal(secret)" :disabled="!!revealBusy[secret.Key]" :aria-pressed="String(isRevealed(secret))" :title="isRevealed(secret) ? 'Hide the value' : revealTooltip">{{isRevealed(secret) ? 'Hide' : 'Reveal'}}</button>
										<button type="button" class="copyBtn" @click="copyValue(secret)" :disabled="!!revealBusy[secret.Key]" :title="copyTooltip">Copy</button>
									</template>
								</template>
							</div>
							<div v-if="isRevealed(secret)" class="revealedRow">
								<span class="secretValue">{{revealed[secret.Key].value}}</span>
								<span class="mutedText revealedHint">hidden again after {{revealSeconds}} s</span>
							</div>
							<div v-if="secret.AdoptedFromAgentName" class="noteText" title="This secret was found on the agent and adopted into the Secrets Manager, scoped to that agent.">Adopted from agent {{secret.AdoptedFromAgentName}}</div>
							<div v-if="secret.OutOfScopeHolders && secret.OutOfScopeHolders.length" class="warningRow">
								<span class="warningChip" :title="outOfScopeTooltip(secret)">also on {{secret.OutOfScopeHolders.length}} out-of-scope agent{{secret.OutOfScopeHolders.length === 1 ? '' : 's'}}</span>
								<button v-if="removableHolders(secret).length" type="button" class="copyBtn" @click="openRemovalDialog(secret)" :title="'Choose agents outside the scope to remove \'' + secret.Key + '\' from'">Remove…</button>
							</div>
							<div v-if="secret.PendingRemovals && secret.PendingRemovals.length" class="noteText pendingNote" :title="pendingRemovalTooltip(secret)">Removal pending from {{secret.PendingRemovals.map(a => a.AgentName).join(', ')}}</div>
						</div>
						<div><span class="syncBadge" :class="syncBadgeClass(secret)" :title="secret.SyncTooltip">{{secret.SyncSummary}}</span></div>
						<div class="lastEditCell">{{formatLastEdit(secret)}}</div>
					</div>
					<p v-if="!filteredSecrets.length" class="emptyMessage">{{secrets.length ? 'No secrets match the filter.' : 'No secrets exist yet. Add one, or authorize an agent to discover the secrets it already has.'}}</p>
				</template>
			</div>
			<div class="card">
				<div class="hintText downloadHint">Download a copy of <code class="inline">secrets.json</code> suitable for placing on a new server that is not connected via TdsAppReleaseAgent. Write-only secrets are left out. Downloads are logged in the user activity log.</div>
				<slide-button v-if="!downloadRevealed"
							  :key="'slideBtn_download_' + downloadSlideBtnCounter"
							  :auto-width="false"
							  :circle="true"
							  :noanimate="true"
							  :width="300"
							  :height="60"
							  text="slide to download secrets.json"
							  success-text="revealed"
							  @completed="onDownloadRevealed"
							  :requireReleaseToComplete="true" />
				<a v-else class="gradientButton green downloadLink" href="#" @click.prevent="downloadSecretsJson">Download secrets.json</a>
				<div class="downloadResult" role="status">
					<template v-if="downloadExcludedKeys && downloadExcludedKeys.length">
						Left out of the download (write-only):
						<template v-for="(k, i) in downloadExcludedKeys" :key="k"><code class="inline excludedKey">{{k}}</code><template v-if="i < downloadExcludedKeys.length - 1">, </template></template>
					</template>
					<template v-else-if="downloadExcludedKeys">No secrets were left out of the download.</template>
				</div>
			</div>
		</template>

		<!-- Edit/Add dialog -->
		<div v-if="editDialogVisible" class="secretDialogOverlay" @click.self="tryCloseDialog">
			<div ref="editDialogEl" class="secretDialog" role="dialog" aria-modal="true" aria-labelledby="secretDialogTitle">
				<div id="secretDialogTitle" class="secretDialogTitle">{{isNewSecret ? 'Add Secret' : 'Edit Secret'}}</div>

				<!-- Narrowing confirmation: nothing was saved yet. -->
				<div v-if="leavingConfirmation" class="leavingPanel">
					<div ref="leavingHeading" class="leavingHeading" tabindex="-1">Agents leaving the scope still hold this secret</div>
					<p class="hintText">The new scope leaves out these agents, and their last sync found <code class="inline">{{leavingConfirmation.request.oldKey}}</code> on them. Nothing has been saved yet.</p>
					<ul class="leavingList">
						<li v-for="a in leavingConfirmation.agents" :key="a.AgentId">{{a.AgentName}} <span class="mutedText">({{a.SameValue ? 'same value' : 'different value'}})</span></li>
					</ul>
					<p class="leavingQuestion">Also remove the secret from these agents? If you leave their copies, they keep the value they have now and receive no further changes.</p>
					<div v-if="editError" class="errorBox" role="alert">{{editError}}</div>
					<div class="secretDialogButtons wrapButtons">
						<button type="button" class="gradientButton tdsHealthBrown" @click="confirmLeaving(true)" :disabled="saving">Save and remove from them</button>
						<button type="button" class="gradientButton green" @click="confirmLeaving(false)" :disabled="saving">Save and leave their copies</button>
						<button type="button" class="gradientButton lightBlue" @click="cancelLeaving" :disabled="saving">Back</button>
					</div>
				</div>

				<div v-else>
					<label class="fieldLabel" for="secretNameInput">Name</label>
					<input id="secretNameInput" ref="keyInput" type="text" class="fieldInput" v-model="editKey" spellcheck="false" autocomplete="off" placeholder="e.g. MyApiKey" />

					<label class="fieldLabel" for="secretDescriptionInput">Description</label>
					<textarea id="secretDescriptionInput" class="fieldInput descriptionInput" v-model="editDescription" rows="3" placeholder="What the secret is for and who uses it"></textarea>

					<fieldset class="fieldGroup">
						<legend class="fieldLabel">Scope</legend>
						<label class="radioRow"><input type="radio" name="secretScopeKind" value="AllAgents" v-model="editScopeKind" /> All agents <span class="mutedText">— every authorized agent receives it</span></label>
						<label class="radioRow"><input type="radio" name="secretScopeKind" value="OrchestratorOnly" v-model="editScopeKind" /> TdsAppService only <span class="mutedText">— no agent receives it</span></label>
						<label class="radioRow"><input type="radio" name="secretScopeKind" value="SelectedAgents" v-model="editScopeKind" /> Selected agents</label>
						<div v-if="editScopeKind === 'SelectedAgents'" class="scopeAgentList" role="group" aria-label="Agents that receive the secret">
							<div v-if="scopeAgentsLoading" class="loadingRow"><ScaleLoader /> Loading agents…</div>
							<div v-else-if="scopeAgentsError" class="errorBox">{{scopeAgentsError}}</div>
							<p v-else-if="!scopeAgentChoices.length" class="emptyMessage">No agents are configured.</p>
							<template v-if="!scopeAgentsLoading">
								<label v-for="a in scopeAgentChoices" :key="a.AgentId" class="scopeAgentItem">
									<input type="checkbox" :value="a.AgentId" v-model="editScopeAgentIds" />
									<span class="statusDot" :class="a.IsConnected ? 'dotGreen' : 'dotGray'" :title="a.IsConnected ? 'connected' : 'disconnected'"></span>
									<span class="scopeAgentText">
										<span class="scopeAgentName">{{a.AgentName}}</span>
										<span v-if="a.Environments && a.Environments.length" class="mutedText"> ({{a.Environments.join(', ')}})</span>
										<span v-if="!a.IsConfigured" class="notSyncedText"> not configured</span>
										<span v-else-if="!a.IsAuthorized" class="notAuthorizedText"> not authorized: it will not receive the secret until it is authorized</span>
										<span v-if="a.IsConfigured && !a.IsConnected" class="mutedText"> (disconnected)</span>
									</span>
								</label>
							</template>
						</div>
					</fieldset>

					<div class="fieldGroup">
						<label class="checkRow"><input type="checkbox" :checked="editWriteOnly" :disabled="editOriginalWriteOnly || saving" @change="onWriteOnlyChange" aria-describedby="secretWriteOnlyHint" /> Write-only</label>
						<div id="secretWriteOnlyHint" class="hintText fieldHint">{{editOriginalWriteOnly ? 'This secret is write-only, which can not be turned off. To read the value again, replace it.' : 'The value can never be revealed, copied or downloaded, only replaced. This can not be undone.'}}</div>
					</div>

					<div v-if="!isNewSecret" class="fieldGroup">
						<div class="fieldLabel">Value</div>
						<div class="currentValueRow">
							Current value:
							<span v-if="editSecret && !editSecret.HasValue" class="blankValue">&lt;null&gt;</span>
							<span v-else-if="editSecret && editSecret.IsBlank" class="blankValue">&lt;blank&gt;</span>
							<span v-else-if="editSecret && editSecret.Fingerprint" class="fingerprint" :title="fingerprintTooltip">{{editSecret.Fingerprint}}</span>
							<span v-else-if="editSecret" class="blankValue" :title="noFingerprintTooltip">value set, no fingerprint yet</span>
						</div>
						<label class="checkRow"><input type="checkbox" v-model="editReplaceValue" :disabled="saving" /> Replace value</label>
					</div>
					<template v-if="isNewSecret || editReplaceValue">
						<label class="fieldLabel" for="secretValueInput">{{isNewSecret ? 'Value' : 'New value'}}</label>
						<textarea id="secretValueInput" ref="valueInput" class="fieldInput valueInput" v-model="editValue" rows="5" spellcheck="false" autocomplete="off"></textarea>
						<div class="generateRow">
							<button type="button" class="copyBtn generateBtn" @click="generateRandomValue" :disabled="saving" aria-describedby="secretGenerateHint">Generate random value</button>
							<label class="generateSizeLabel" for="secretGenerateBytes">Size</label>
							<select id="secretGenerateBytes" class="generateSizeSelect" v-model.number="generateByteCount">
								<option v-for="n in generateByteOptions" :key="n" :value="n">{{n}} bytes ({{base64Length(n)}} characters)</option>
							</select>
						</div>
						<div id="secretGenerateHint" class="hintText generateHint">Fills the value with base64 of cryptographically random bytes, generated in this browser.  Nothing is saved until you click Save.</div>
					</template>

					<div v-if="editSecret" class="metadataSection">
						<div class="metaRow"><span class="metaLabel">Created</span>{{formatUtc(editSecret.CreatedUtc)}} by {{editSecret.CreatedBy || 'unknown'}}</div>
						<div class="metaRow"><span class="metaLabel">Modified</span>{{formatUtc(editSecret.ModifiedUtc)}} by {{editSecret.ModifiedBy || 'unknown'}}</div>
						<div v-if="editSecret.AdoptedFromAgentName" class="metaRow"><span class="metaLabel">Adopted</span>from agent {{editSecret.AdoptedFromAgentName}}</div>
						<div class="metaRow"><span class="metaLabel">Sync</span><span class="syncBadge" :class="syncBadgeClass(editSecret)" :title="editSecret.SyncTooltip">{{editSecret.SyncSummary}}</span></div>
						<div v-if="editSecret.InScopeAgents && editSecret.InScopeAgents.length" class="agentSyncList">
							<div v-for="a in editSecret.InScopeAgents" :key="a.AgentId" class="agentSyncItem">
								<span class="statusDot" :class="a.IsSynced ? 'dotGreen' : 'dotRed'"></span>
								<span>{{a.AgentName}}: <span :class="a.IsSynced ? 'syncedText' : 'notSyncedText'">{{a.IsSynced ? 'synced' : 'not synced'}}</span><span v-if="!a.IsConnected" class="mutedText"> (disconnected)</span></span>
							</div>
						</div>
						<div v-if="editSecret.OutOfScopeHolders && editSecret.OutOfScopeHolders.length" class="metaBlock">
							<div class="metaSubheading">Also held by agents outside the scope</div>
							<div v-for="h in editSecret.OutOfScopeHolders" :key="h.AgentId" class="agentSyncItem">
								<span class="statusDot" :class="h.IsConnected ? 'dotGreen' : 'dotGray'" :title="h.IsConnected ? 'connected' : 'disconnected'"></span>
								<span>{{h.AgentName}}: {{h.SameValue ? 'same value' : 'different value'}}<span v-if="h.PendingRemoval" class="mutedText">, removal pending</span><span v-if="!h.IsConnected" class="mutedText"> (disconnected)</span></span>
							</div>
						</div>
						<div v-if="editSecret.PendingRemovals && editSecret.PendingRemovals.length" class="metaBlock">
							<div class="metaSubheading">Removal pending from</div>
							<div v-for="p in editSecret.PendingRemovals" :key="p.AgentId" class="agentSyncItem">
								<span class="statusDot" :class="p.IsConnected ? 'dotGreen' : 'dotGray'" :title="p.IsConnected ? 'connected' : 'disconnected'"></span>
								<span>{{p.AgentName}}<span v-if="!p.IsConfigured || !p.IsAuthorized || !p.IsConnected" class="mutedText"> ({{pendingRemovalState(p)}})</span></span>
							</div>
						</div>
					</div>
					<div v-if="editError" class="errorBox dialogError" role="alert">{{editError}}</div>
					<div class="secretDialogButtons">
						<button type="button" class="gradientButton green" @click="saveSecret" :disabled="saving">Save</button>
						<button type="button" class="gradientButton lightBlue" @click="tryCloseDialog">Cancel</button>
						<button v-if="!isNewSecret" type="button" class="gradientButton tdsHealthBrown deleteBtn" @click="deleteSecret" :disabled="saving">Delete</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Remove out-of-scope copies dialog -->
		<div v-if="removalDialog" class="secretDialogOverlay" @click.self="closeRemovalDialog">
			<div ref="removalDialogEl" class="secretDialog" role="dialog" aria-modal="true" aria-labelledby="removalDialogTitle">
				<div id="removalDialogTitle" ref="removalTitle" class="secretDialogTitle" tabindex="-1">Remove Out-of-Scope Copies</div>
				<p class="hintText">These agents are outside the scope of <code class="inline">{{removalDialog.key}}</code>, but their last sync found the key on them. Sync never deletes these copies by itself. Choose the agents to remove it from; each removal happens at the agent's next sync.</p>
				<div role="group" aria-label="Agents to remove the secret from">
					<label v-for="h in removalDialog.holders" :key="h.AgentId" class="scopeAgentItem">
						<input type="checkbox" :value="h.AgentId" v-model="removalDialog.selectedIds" :disabled="h.PendingRemoval || removalBusy" />
						<span class="statusDot" :class="h.IsConnected ? 'dotGreen' : 'dotGray'" :title="h.IsConnected ? 'connected' : 'disconnected'"></span>
						<span class="scopeAgentText">
							<span class="scopeAgentName">{{h.AgentName}}</span>
							<span class="mutedText"> ({{h.SameValue ? 'same value' : 'different value'}}{{h.IsConnected ? '' : ', disconnected'}})</span>
							<span v-if="h.PendingRemoval" class="mutedText"> removal already pending</span>
						</span>
					</label>
				</div>
				<div v-if="removalDialog.error" class="errorBox dialogError" role="alert">{{removalDialog.error}}</div>
				<div class="secretDialogButtons">
					<button type="button" class="gradientButton tdsHealthBrown" @click="submitRemoval" :disabled="removalBusy || !removalDialog.selectedIds.length">Remove from selected</button>
					<button type="button" class="gradientButton lightBlue" @click="closeRemovalDialog">Cancel</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import { ModalConfirmDialog } from 'appRoot/scripts/ModalDialog';
	import
		{
			GetSecretsList, GetSecretDetail, RevealSecret, SaveSecret, RequestRemoval, DeleteSecret,
			GetAgentAuthorization, SetAgentAuthorization, DownloadSecretsJson
		} from 'appRoot/api/SecretsManagerData';

	/** How long a revealed value stays visible. */
	const RevealSeconds = 30;

	/**
	 * Returns base64 (standard alphabet, padded) of the given number of cryptographically random bytes from
	 * crypto.getRandomValues.  The value stays in the browser until the user saves it; never log it.
	 */
	function RandomBase64(byteCount)
	{
		let bytes = new Uint8Array(byteCount);
		window.crypto.getRandomValues(bytes);
		let binary = '';
		for (let i = 0; i < bytes.length; i++)
			binary += String.fromCharCode(bytes[i]);
		return window.btoa(binary);
	}

	/** True if the two agent id lists hold the same ids, ignoring case and order (agent ids compare case-insensitively on the server). */
	function SameIdSet(a, b)
	{
		let x = (a || []).map(s => String(s).toLowerCase()).sort();
		let y = (b || []).map(s => String(s).toLowerCase()).sort();
		return x.length === y.length && x.every((v, i) => v === y[i]);
	}

	/**
	 * Writes text to the clipboard with the async clipboard API.  There is deliberately no execCommand fallback: it would put
	 * the secret value into the page (a temporary textarea) and cannot reliably report failure.
	 */
	function WriteTextToClipboard(text)
	{
		if (window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function')
			return navigator.clipboard.writeText(text);
		return Promise.reject(new Error('The clipboard is not available on this page.  Use Reveal instead.'));
	}

	export default {
		components: { SlideButton },
		data()
		{
			return {
				error: null,
				loadingSecrets: true,
				loadingAgents: false,
				secrets: [],
				agents: [],
				filterText: '',
				/** Values revealed by the user, by key: { value, fingerprint }.  Each is removed after RevealSeconds. */
				revealed: {},
				/** Keys with a RevealSecret request in flight. */
				revealBusy: {},
				/** Set when the page is left, so a RevealSecret response arriving afterwards is discarded. */
				isUnmounted: false,
				revealSeconds: RevealSeconds,
				downloadRevealed: false,
				downloadSlideBtnCounter: 0,
				/** Keys left out of the last download (write-only), or null before the first download. */
				downloadExcludedKeys: null,
				agentSlideBtnCounters: {},
				editDialogVisible: false,
				editOriginalKey: null,
				editKey: '',
				editOriginalDescription: '',
				editDescription: '',
				editOriginalScopeKind: 'AllAgents',
				editScopeKind: 'AllAgents',
				editOriginalScopeAgentIds: [],
				editScopeAgentIds: [],
				editOriginalWriteOnly: false,
				editWriteOnly: false,
				editReplaceValue: false,
				editValue: '',
				editSecret: null,
				editError: null,
				/** Set when SaveSecret asked for confirmation: { request, agents }.  Nothing has been saved. */
				leavingConfirmation: null,
				scopeAgents: [],
				scopeAgentsLoading: false,
				scopeAgentsError: null,
				scopeAgentsToken: 0,
				saving: false,
				/** { key, holders, selectedIds, error } while the out-of-scope removal dialog is open. */
				removalDialog: null,
				removalBusy: false,
				/** Number of open ModalConfirmDialogs started by this page; Escape is theirs while any is open. */
				modalDepth: 0,
				generateByteCount: 32,
				generateByteOptions: [32, 48, 64],
				refreshTimer: null,
				silentRefreshBusy: false,
				fingerprintTooltip: 'Fingerprint of the value. It changes when the value changes. Equal fingerprints mean equal values.',
				noFingerprintTooltip: 'The secret has a value, but no fingerprint can be computed until the Secrets Manager creates its master key file, at its first save.',
				writeOnlyTooltip: 'Write-only: the value can not be revealed, copied or downloaded. To read it again, replace its value.',
				revealTooltip: 'Show the value here for ' + RevealSeconds + ' seconds. Every reveal is logged in the user activity log.',
				copyTooltip: 'Copy the value to the clipboard without showing it. Copying reveals the value, and every reveal is logged in the user activity log.',
			};
		},
		computed:
		{
			isAgentsView()
			{
				return this.$route.name === 'clientSecretsAgents';
			},
			isNewSecret()
			{
				return !this.editOriginalKey;
			},
			editDirty()
			{
				if (!this.editDialogVisible)
					return false;
				if (this.editKey !== (this.editOriginalKey || ''))
					return true;
				if (this.editDescription !== this.editOriginalDescription)
					return true;
				if (this.editScopeKind !== this.editOriginalScopeKind)
					return true;
				if (this.editScopeKind === 'SelectedAgents' && !SameIdSet(this.editScopeAgentIds, this.editOriginalScopeAgentIds))
					return true;
				if (this.editWriteOnly !== this.editOriginalWriteOnly)
					return true;
				if (this.isNewSecret ? this.editValue !== '' : this.editReplaceValue)
					return true;
				return false;
			},
			filteredSecrets()
			{
				let filter = (this.filterText || '').trim().toLowerCase();
				if (!filter)
					return this.secrets;
				return this.secrets.filter(s =>
				{
					let key = (s.Key || '').toLowerCase();
					let description = (s.Description || '').toLowerCase();
					return key.indexOf(filter) >= 0 || description.indexOf(filter) >= 0;
				});
			},
			/** Configured agents for the scope checklist, plus agents in the secret's scope that are no longer configured. */
			scopeAgentChoices()
			{
				let list = this.scopeAgents.map(a => Object.assign({ IsConfigured: true }, a));
				let known = new Set(list.map(a => String(a.AgentId).toLowerCase()));
				let scoped = (this.editSecret && this.editSecret.ScopeAgents) || [];
				for (let i = 0; i < scoped.length; i++)
				{
					let sa = scoped[i];
					if (!known.has(String(sa.AgentId).toLowerCase()))
						list.push({ AgentId: sa.AgentId, AgentName: sa.AgentName, Environments: [], IsAuthorized: sa.IsAuthorized, IsConnected: false, IsConfigured: !!sa.IsConfigured });
				}
				return list;
			}
		},
		watch:
		{
			isAgentsView(val)
			{
				if (val)
				{
					this.hideAllReveals();
					this.loadAgents();
				}
				else
					this.loadSecrets();
			},
			editReplaceValue(replace)
			{
				// Never keep a value the user typed and then abandoned.
				this.editValue = '';
				if (replace)
				{
					this.$nextTick(() =>
					{
						if (this.$refs.valueInput)
							this.$refs.valueInput.focus();
					});
				}
			},
			editDialogVisible(visible)
			{
				if (visible)
				{
					this.$nextTick(() =>
					{
						if (this.$refs.keyInput)
							this.$refs.keyInput.focus();
					});
				}
			}
		},
		created()
		{
			/** setTimeout handles of revealed values, by key.  Not reactive. */
			this.revealTimers = Object.create(null);
			/** Element to focus again when the open dialog closes, and the key of the secret whose name button to focus if that element is gone.  Not reactive. */
			this.returnFocusEl = null;
			this.returnFocusKey = null;
			/** Key of the secret whose name button gets focus when the list has reloaded.  Not reactive. */
			this.pendingFocusKey = null;
			document.addEventListener('keydown', this.onDocumentKeyDown);
			document.addEventListener('keyup', this.onDocumentKeyUp);
			if (this.isAgentsView)
				this.loadAgents();
			else
				this.loadSecrets();
			// Sync with agents runs in the background on the server, so refresh silently to keep sync status current.
			this.refreshTimer = setInterval(this.silentRefresh, 8000);
		},
		beforeUnmount()
		{
			this.isUnmounted = true;
			clearInterval(this.refreshTimer);
			document.removeEventListener('keydown', this.onDocumentKeyDown);
			document.removeEventListener('keyup', this.onDocumentKeyUp);
			this.hideAllReveals();
			this.editValue = '';
		},
		methods:
		{
			loadSecrets()
			{
				this.loadingSecrets = true;
				this.error = null;
				GetSecretsList()
					.then(result =>
					{
						this.loadingSecrets = false;
						if (result.error)
						{
							this.error = result.error;
							return;
						}
						this.applySecrets(result.secrets || []);
						if (this.pendingFocusKey)
						{
							let key = this.pendingFocusKey;
							this.pendingFocusKey = null;
							this.$nextTick(() => { this.focusSecretButton(key); });
						}
					})
					.catch(ex =>
					{
						this.loadingSecrets = false;
						this.error = ex.message || String(ex);
					});
			},
			/** Focuses the name button of the given secret, or the Add Secret button if the secret is not listed. */
			focusSecretButton(key)
			{
				let buttons = this.$el.querySelectorAll('.secretNameBtn');
				for (let i = 0; i < buttons.length; i++)
				{
					if (buttons[i].getAttribute('data-secret-key') === key)
					{
						buttons[i].focus();
						return;
					}
				}
				if (this.$refs.addSecretBtn)
					this.$refs.addSecretBtn.focus();
			},
			/** Remembers where focus is as a dialog opens, so closing the dialog can put it back. */
			rememberFocus(key)
			{
				this.returnFocusEl = document.activeElement;
				this.returnFocusKey = key || null;
			},
			/** Puts focus back where it was when the dialog opened; if that element is gone (the list reloads after a save), on the secret's name button once the list is back. */
			restoreFocus()
			{
				let el = this.returnFocusEl;
				let key = this.returnFocusKey;
				this.returnFocusEl = null;
				this.returnFocusKey = null;
				this.$nextTick(() =>
				{
					if (el && el !== document.body && document.body.contains(el) && typeof el.focus === 'function')
						el.focus();
					else if (key && this.loadingSecrets)
						this.pendingFocusKey = key;
					else
						this.focusSecretButton(key);
				});
			},
			/** Applies a new secrets list, and hides revealed values whose secret is gone, became write-only, or changed value. */
			applySecrets(secrets)
			{
				this.secrets = secrets;
				let keys = Object.keys(this.revealed);
				for (let i = 0; i < keys.length; i++)
				{
					let s = secrets.find(x => x.Key === keys[i]);
					if (!s || s.WriteOnly || s.Fingerprint !== this.revealed[keys[i]].fingerprint)
						this.hideReveal(keys[i]);
				}
			},
			loadAgents()
			{
				this.loadingAgents = true;
				this.error = null;
				GetAgentAuthorization()
					.then(result =>
					{
						this.loadingAgents = false;
						if (result.error)
						{
							this.error = result.error;
							return;
						}
						this.applyAgents(result.agents || []);
					})
					.catch(ex =>
					{
						this.loadingAgents = false;
						this.error = ex.message || String(ex);
					});
			},
			applyAgents(agents)
			{
				this.agents = agents;
				for (let i = 0; i < this.agents.length; i++)
				{
					let id = this.agents[i].AgentId;
					if (typeof this.agentSlideBtnCounters[id] === 'undefined')
						this.agentSlideBtnCounters[id] = 0;
				}
			},
			silentRefresh()
			{
				if (document.hidden || this.silentRefreshBusy)
					return;
				if (this.isAgentsView)
				{
					if (this.loadingAgents)
						return;
					this.silentRefreshBusy = true;
					GetAgentAuthorization()
						.then(result =>
						{
							// Only apply the new data if something changed, so re-rendering does not disturb an in-progress slide gesture.
							if (!result.error && result.agents && JSON.stringify(result.agents) !== JSON.stringify(this.agents))
								this.applyAgents(result.agents);
						})
						.catch(() => { })
						.finally(() => { this.silentRefreshBusy = false; });
				}
				else
				{
					if (this.loadingSecrets)
						return;
					this.silentRefreshBusy = true;
					GetSecretsList()
						.then(result =>
						{
							// Only apply the new data if something changed, so re-rendering does not clear a text selection the user made.
							if (!result.error && result.secrets && JSON.stringify(result.secrets) !== JSON.stringify(this.secrets))
								this.applySecrets(result.secrets);
						})
						.catch(() => { })
						.finally(() => { this.silentRefreshBusy = false; });
				}
			},
			secretKeyExists(key, ignoreKey)
			{
				let normalizedKey = (key || '').toLowerCase();
				let normalizedIgnore = ignoreKey ? ignoreKey.toLowerCase() : null;
				return this.secrets.some(s =>
				{
					let existingKey = (s.Key || '').toLowerCase();
					if (normalizedIgnore && existingKey === normalizedIgnore)
						return false;
					return existingKey === normalizedKey;
				});
			},
			scopeLabel(secret)
			{
				if (secret.ScopeKind === 'OrchestratorOnly')
					return 'TdsAppService only';
				if (secret.ScopeKind === 'SelectedAgents')
				{
					let n = (secret.ScopeAgents || []).length;
					return n + ' agent' + (n === 1 ? '' : 's');
				}
				return 'All agents';
			},
			scopeTooltip(secret)
			{
				if (secret.ScopeKind === 'OrchestratorOnly')
					return 'No agent receives this secret. Only TdsAppService reads it.';
				if (secret.ScopeKind === 'SelectedAgents')
				{
					let lines = (secret.ScopeAgents || []).map(a =>
					{
						if (!a.IsConfigured)
							return a.AgentName + ' (not configured)';
						if (!a.IsAuthorized)
							return a.AgentName + ' (not authorized: does not receive it until authorized)';
						return a.AgentName;
					});
					return 'Only these agents receive this secret:\n' + lines.join('\n');
				}
				return 'Every authorized agent receives this secret.';
			},
			outOfScopeTooltip(secret)
			{
				let lines = (secret.OutOfScopeHolders || []).map(h =>
					h.AgentName + ': ' + (h.SameValue ? 'same value' : 'different value')
					+ (h.PendingRemoval ? ', removal pending' : '')
					+ (h.IsConnected ? '' : ', disconnected'));
				return 'These agents are outside the scope, but their last sync found the key on them. Sync never deletes these copies by itself.\n' + lines.join('\n');
			},
			removableHolders(secret)
			{
				return (secret.OutOfScopeHolders || []).filter(h => !h.PendingRemoval);
			},
			pendingRemovalState(p)
			{
				if (!p.IsConfigured)
					return 'not configured';
				if (!p.IsAuthorized)
					return 'waits until the agent is authorized';
				if (!p.IsConnected)
					return 'waits until the agent connects';
				return '';
			},
			pendingRemovalTooltip(secret)
			{
				let lines = (secret.PendingRemovals || []).map(p =>
				{
					let state = this.pendingRemovalState(p);
					return p.AgentName + (state ? ' (' + state + ')' : '');
				});
				return 'Sync deletes the key from these agents at their next sync:\n' + lines.join('\n');
			},
			syncBadgeClass(secret)
			{
				if (!secret || typeof secret.TotalAgentCount === 'undefined' || secret.OrchestratorOnly)
					return 'syncNoAgents';
				if (secret.TotalAgentCount === 0)
					return 'syncNoAgents';
				if (secret.SyncedAgentCount === secret.TotalAgentCount)
					return 'syncAll';
				if (secret.SyncedAgentCount > 0)
					return 'syncPartial';
				return 'syncNone';
			},
			isRevealed(secret)
			{
				let r = this.revealed[secret.Key];
				return !!r && r.fingerprint === secret.Fingerprint;
			},
			/** Calls RevealSecret (audited on the server).  Resolves with the value; rejects with an Error carrying the server's message. */
			fetchSecretValue(key)
			{
				this.revealBusy[key] = true;
				return RevealSecret(key)
					.then(result =>
					{
						if (result.error)
							throw new Error(result.error);
						return result.value;
					})
					.finally(() => { delete this.revealBusy[key]; });
			},
			toggleReveal(secret)
			{
				let key = secret.Key;
				if (this.isRevealed(secret))
				{
					this.hideReveal(key);
					return;
				}
				let fingerprint = secret.Fingerprint;
				this.fetchSecretValue(key)
					.then(value =>
					{
						if (this.isAgentsView || this.isUnmounted)
							return;
						this.hideReveal(key);
						this.revealed[key] = { value: value == null ? '' : value, fingerprint };
						this.revealTimers[key] = setTimeout(() => { this.hideReveal(key); }, RevealSeconds * 1000);
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					});
			},
			hideReveal(key)
			{
				if (this.revealTimers && this.revealTimers[key])
				{
					clearTimeout(this.revealTimers[key]);
					delete this.revealTimers[key];
				}
				if (this.revealed[key])
					delete this.revealed[key];
			},
			hideAllReveals()
			{
				let keys = Object.keys(this.revealed);
				for (let i = 0; i < keys.length; i++)
					this.hideReveal(keys[i]);
			},
			/** Reveals the value (audited) and copies it to the clipboard without displaying it. */
			copyValue(secret)
			{
				let key = secret.Key;
				let valuePromise = this.fetchSecretValue(key).then(value => value == null ? '' : value);
				let copied;
				// Safari only allows a clipboard write that starts within the click, so hand the clipboard a pending item where supported.
				let canWriteItem = window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.write === 'function' && typeof window.ClipboardItem === 'function';
				if (canWriteItem)
				{
					try
					{
						let blobPromise = valuePromise.then(value => new Blob([value], { type: 'text/plain' }));
						blobPromise.catch(() => { }); // A failed reveal is reported below; do not also log an unhandled rejection.
						let item = new window.ClipboardItem({ 'text/plain': blobPromise });
						copied = navigator.clipboard.write([item]).catch(() => valuePromise.then(WriteTextToClipboard));
					}
					catch (ex)
					{
						copied = valuePromise.then(WriteTextToClipboard);
					}
				}
				else
					copied = valuePromise.then(WriteTextToClipboard);
				copied
					.then(() =>
					{
						toaster.Success('Copied the value of "' + key + '" to the clipboard.');
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					});
			},
			formatLastEdit(secret)
			{
				return this.formatUtc(secret.ModifiedUtc) + ',\n by ' + (secret.ModifiedBy || 'unknown');
			},
			formatUtc(utcStr)
			{
				if (!utcStr)
					return '';
				let d = new Date(utcStr);
				if (isNaN(d.getTime()))
					return utcStr;
				return d.toLocaleString();
			},
			/** ModalConfirmDialog, tracked so this page's Escape handling leaves the modal's Escape alone. */
			confirmModal(message, title, yesText, noText)
			{
				this.modalDepth++;
				return ModalConfirmDialog(message, title, yesText, noText)
					.then(confirmed =>
					{
						this.modalDepth--;
						return confirmed;
					});
			},
			/** Keeps Tab and Shift+Tab inside the open dialog. */
			onDocumentKeyDown(e)
			{
				if (e.key !== 'Tab' || this.modalDepth > 0)
					return;
				let dialog = this.removalDialog ? this.$refs.removalDialogEl : (this.editDialogVisible ? this.$refs.editDialogEl : null);
				if (!dialog)
					return;
				let focusable = Array.prototype.filter.call(
					dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
					el => !el.disabled && el.getClientRects().length > 0);
				if (!focusable.length)
					return;
				let first = focusable[0];
				let last = focusable[focusable.length - 1];
				let active = document.activeElement;
				let index = focusable.indexOf(active);
				let target = null;
				if (!dialog.contains(active))
					target = e.shiftKey ? last : first;
				else if (e.shiftKey && (index === 0 || (index < 0 && !focusable.some(el => el.compareDocumentPosition(active) & Node.DOCUMENT_POSITION_FOLLOWING))))
					target = last; // Nothing focusable before the focused element.
				else if (!e.shiftKey && (index === focusable.length - 1 || (index < 0 && !focusable.some(el => active.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING))))
					target = first; // Nothing focusable after the focused element.
				if (target)
				{
					e.preventDefault();
					target.focus();
				}
			},
			/** Escape closes the open dialog.  Handled on keyup, like ModalDialog, so the keyup of the Escape that opened a confirmation does not also close it. */
			onDocumentKeyUp(e)
			{
				if (e.key !== 'Escape' || e.defaultPrevented || this.modalDepth > 0)
					return;
				if (this.removalDialog)
					this.closeRemovalDialog();
				else if (this.editDialogVisible)
				{
					if (this.leavingConfirmation)
					{
						if (!this.saving)
							this.cancelLeaving();
					}
					else
						this.tryCloseDialog();
				}
			},
			base64Length(byteCount)
			{
				return 4 * Math.ceil(byteCount / 3);
			},
			generateRandomValue()
			{
				if (this.modalDepth > 0)
					return;
				if (!window.crypto || typeof window.crypto.getRandomValues !== 'function')
				{
					toaster.Error('This browser does not provide a cryptographically strong random number generator.');
					return;
				}
				let byteCount = this.generateByteCount;
				let apply = () =>
				{
					this.editValue = RandomBase64(byteCount);
				};
				if (!this.editValue)
				{
					apply();
					return;
				}
				this.confirmModal('Replace the current value with a new random value?\n\nNothing is saved until you click Save.', 'Generate Random Value', 'Replace', 'Cancel')
					.then(confirmed =>
					{
						if (confirmed)
							apply();
					});
			},
			resetEditState()
			{
				this.editError = null;
				this.leavingConfirmation = null;
				this.editReplaceValue = false;
				this.editValue = '';
				this.saving = false;
			},
			openSecretDialog(secret)
			{
				this.resetEditState();
				this.rememberFocus(secret.Key);
				this.editOriginalKey = secret.Key;
				this.editKey = secret.Key;
				this.editOriginalDescription = secret.Description || '';
				this.editDescription = this.editOriginalDescription;
				this.editOriginalScopeKind = secret.ScopeKind || 'AllAgents';
				this.editScopeKind = this.editOriginalScopeKind;
				this.editOriginalScopeAgentIds = (secret.ScopeAgentIds || []).slice();
				this.editScopeAgentIds = this.editOriginalScopeAgentIds.slice();
				this.editOriginalWriteOnly = !!secret.WriteOnly;
				this.editWriteOnly = this.editOriginalWriteOnly;
				this.editSecret = secret;
				this.editDialogVisible = true;
				this.loadScopeAgents();
				GetSecretDetail(secret.Key)
					.then(result =>
					{
						if (!result.error && result.secret && this.editDialogVisible && this.editOriginalKey === secret.Key)
							this.editSecret = result.secret;
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					});
			},
			openNewSecretDialog()
			{
				this.resetEditState();
				this.rememberFocus(null);
				this.editOriginalKey = null;
				this.editKey = '';
				this.editOriginalDescription = '';
				this.editDescription = '';
				this.editOriginalScopeKind = 'AllAgents';
				this.editScopeKind = 'AllAgents';
				this.editOriginalScopeAgentIds = [];
				this.editScopeAgentIds = [];
				this.editOriginalWriteOnly = false;
				this.editWriteOnly = false;
				this.editSecret = null;
				this.editDialogVisible = true;
				this.loadScopeAgents();
			},
			/** Loads the configured agents for the scope checklist. */
			loadScopeAgents()
			{
				let token = ++this.scopeAgentsToken;
				this.scopeAgentsLoading = true;
				this.scopeAgentsError = null;
				GetAgentAuthorization()
					.then(result =>
					{
						if (token !== this.scopeAgentsToken)
							return;
						this.scopeAgentsLoading = false;
						if (result.error)
						{
							this.scopeAgentsError = result.error;
							return;
						}
						this.scopeAgents = result.agents || [];
						// Match the stored ids to the configured agents' spelling, so their checkboxes show as checked.
						this.editScopeAgentIds = this.editScopeAgentIds.map(id =>
						{
							let match = this.scopeAgents.find(a => String(a.AgentId).toLowerCase() === String(id).toLowerCase());
							return match ? match.AgentId : id;
						});
					})
					.catch(ex =>
					{
						if (token !== this.scopeAgentsToken)
							return;
						this.scopeAgentsLoading = false;
						this.scopeAgentsError = ex.message || String(ex);
					});
			},
			closeEditDialog()
			{
				this.editDialogVisible = false;
				this.resetEditState();
				this.restoreFocus();
			},
			tryCloseDialog()
			{
				if (this.modalDepth > 0)
					return;
				if (!this.editDirty)
				{
					this.closeEditDialog();
					return;
				}
				this.confirmModal('You have unsaved changes.', 'Unsaved Changes', 'Close', 'Stay Open')
					.then(result =>
					{
						if (result)
							this.closeEditDialog();
					});
			},
			onWriteOnlyChange(e)
			{
				let checked = e.target.checked;
				if (!checked || this.isNewSecret)
				{
					this.editWriteOnly = checked;
					return;
				}
				// Turning write-only on for an existing secret is one-way; confirm first.
				e.target.checked = false;
				this.confirmModal('Make "' + this.editOriginalKey + '" write-only?\n\nAfter you save, its value can never be revealed, copied or downloaded again, only replaced. This can not be undone.', 'Write-Only', 'Make Write-Only', 'Cancel')
					.then(confirmed =>
					{
						this.editWriteOnly = !!confirmed;
					});
			},
			saveSecret()
			{
				this.editError = null;
				let key = this.editKey ? this.editKey.trim() : '';
				if (!key)
				{
					this.editError = 'Secret name is required.';
					return;
				}
				if (!/^[A-Za-z_][A-Za-z_0-9.]*$/.test(key))
				{
					this.editError = 'Secret name must be a valid variable identifier: only letters, digits, underscore, and period, not beginning with a digit or period.';
					return;
				}
				if (this.secretKeyExists(key, this.editOriginalKey))
				{
					this.editError = 'A secret with that name already exists.';
					return;
				}
				if (this.editScopeKind === 'SelectedAgents' && !this.editScopeAgentIds.length)
				{
					this.editError = 'Select at least one agent, or choose the scope "TdsAppService only".';
					return;
				}
				let isNew = this.isNewSecret;
				let setValue = isNew || this.editReplaceValue;
				let request = {
					oldKey: isNew ? '' : this.editOriginalKey,
					key: key,
					setValue: setValue,
					value: setValue ? this.editValue : null,
					// Null keeps the stored description; an empty string clears it.
					description: (!isNew && this.editDescription === this.editOriginalDescription) ? null : this.editDescription,
					scopeKind: this.editScopeKind,
					scopeAgentIds: this.editScopeKind === 'SelectedAgents' ? this.editScopeAgentIds.slice() : null,
					writeOnly: (isNew || this.editWriteOnly !== this.editOriginalWriteOnly) ? this.editWriteOnly : null,
					removeFromAgentsLeavingScope: null
				};
				this.submitSave(request);
			},
			submitSave(request)
			{
				this.saving = true;
				this.editError = null;
				SaveSecret(request)
					.then(result =>
					{
						this.saving = false;
						if (result.error)
						{
							this.editError = result.error;
							return;
						}
						if (result.needsConfirmation)
						{
							this.leavingConfirmation = { request, agents: result.agentsLeavingScope || [] };
							this.$nextTick(() =>
							{
								if (this.$refs.leavingHeading)
									this.$refs.leavingHeading.focus();
							});
							return;
						}
						if (result.message)
							toaster.Info(result.message);
						this.returnFocusKey = request.key; // The list reloads, so focus returns to the (possibly renamed) secret's name button.
						this.closeEditDialog();
						this.loadSecrets();
					})
					.catch(ex =>
					{
						this.saving = false;
						this.editError = ex.message || String(ex);
					});
			},
			/** Re-submits the request that needed confirmation, unchanged except for the answer. */
			confirmLeaving(removeFromAgents)
			{
				if (!this.leavingConfirmation || this.saving)
					return;
				let request = Object.assign({}, this.leavingConfirmation.request, { removeFromAgentsLeavingScope: !!removeFromAgents });
				this.submitSave(request);
			},
			cancelLeaving()
			{
				this.leavingConfirmation = null;
				this.editError = null;
				// The panel's buttons are gone; keep focus in the dialog.
				this.$nextTick(() =>
				{
					if (this.$refs.keyInput)
						this.$refs.keyInput.focus();
				});
			},
			deleteSecret()
			{
				if (!this.editOriginalKey)
					return;
				let agentsNote = this.editOriginalScopeKind === 'OrchestratorOnly'
					? 'No agent receives this secret, so no agent copy is deleted.'
					: 'Sync also deletes it from the authorized agents in its scope, and from agents with a pending removal. Copies on agents outside its scope are left alone.';
				this.confirmModal('Delete secret "' + this.editOriginalKey + '"?\n\n' + agentsNote, 'Delete Secret', 'Delete', 'Cancel')
					.then(confirmed =>
					{
						if (!confirmed)
							return;
						this.saving = true;
						this.editError = null;
						DeleteSecret(this.editOriginalKey)
							.then(result =>
							{
								this.saving = false;
								if (result.error)
								{
									this.editError = result.error;
									return;
								}
								this.closeEditDialog();
								this.loadSecrets();
							})
							.catch(ex =>
							{
								this.saving = false;
								this.editError = ex.message || String(ex);
							});
					});
			},
			openRemovalDialog(secret)
			{
				let holders = (secret.OutOfScopeHolders || []).slice();
				this.rememberFocus(secret.Key);
				this.removalDialog = {
					key: secret.Key,
					holders: holders,
					selectedIds: holders.filter(h => !h.PendingRemoval).map(h => h.AgentId),
					error: null
				};
				this.$nextTick(() =>
				{
					if (this.$refs.removalTitle)
						this.$refs.removalTitle.focus();
				});
			},
			closeRemovalDialog()
			{
				if (this.removalBusy || this.modalDepth > 0)
					return;
				this.removalDialog = null;
				this.restoreFocus();
			},
			submitRemoval()
			{
				let dialog = this.removalDialog;
				if (!dialog || !dialog.selectedIds.length || this.removalBusy)
					return;
				let ids = dialog.selectedIds.slice();
				let names = dialog.holders.filter(h => ids.indexOf(h.AgentId) >= 0).map(h => h.AgentName);
				this.confirmModal('Remove secret "' + dialog.key + '" from these agents?\n\n' + names.join('\n') + '\n\nSync deletes the key from each agent at its next sync. Apps on those machines that still read the key will no longer find it.', 'Remove Out-of-Scope Copies', 'Remove', 'Cancel')
					.then(confirmed =>
					{
						if (!confirmed || this.removalDialog !== dialog)
							return;
						this.removalBusy = true;
						dialog.error = null;
						RequestRemoval(dialog.key, ids)
							.then(result =>
							{
								this.removalBusy = false;
								if (result.error)
								{
									dialog.error = result.error;
									return;
								}
								toaster.Success('Removal of "' + dialog.key + '" requested from ' + names.join(', ') + '.');
								this.removalDialog = null;
								// The Remove… button disappears once every holder is pending removal, so focus the secret's name button.
								this.returnFocusEl = null;
								this.restoreFocus();
								this.silentRefresh();
							})
							.catch(ex =>
							{
								this.removalBusy = false;
								dialog.error = ex.message || String(ex);
							});
					});
			},
			onDownloadRevealed()
			{
				this.downloadRevealed = true;
			},
			downloadSecretsJson()
			{
				DownloadSecretsJson()
					.then(result =>
					{
						if (result.error)
						{
							toaster.Error(result.error);
							return;
						}
						let blob = new Blob([result.secretsJson], { type: 'application/json' });
						let url = URL.createObjectURL(blob);
						let a = document.createElement('a');
						a.href = url;
						a.download = 'secrets.json';
						a.click();
						URL.revokeObjectURL(url);
						this.downloadExcludedKeys = result.excludedWriteOnlyKeys || [];
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					});
			},
			onAgentAuthCommanded(agent)
			{
				let newAuthorized = !agent.IsAuthorized;
				SetAgentAuthorization(agent.AgentId, newAuthorized)
					.then(result =>
					{
						if (result.error)
							toaster.Error(result.error);
						else
							agent.IsAuthorized = newAuthorized;
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					})
					.finally(() =>
					{
						this.agentSlideBtnCounters[agent.AgentId]++;
						this.silentRefresh();
					});
			}
		}
	}
</script>

<style scoped>
	/* Match the page background and padding used by other client pages (e.g. the Release tab's .pageRoot). */
	.secretsManagerRoot
	{
		padding: 8px 8px 1em 8px;
		background-color: #EFEDE6;
		overflow-y: auto;
	}

		/* Keep the content column a comfortable reading width on large monitors.  The dialog overlays are position:fixed and must not be constrained. */
		.secretsManagerRoot > :not(.secretDialogOverlay)
		{
			max-width: 950px;
		}

	.pageHeading
	{
		font-size: 32px;
		margin-bottom: 0.33em;
	}

	/* Tabs */
	.tabRow
	{
		display: flex;
		flex-wrap: wrap;
		border-bottom: 2px solid #CCCCCC;
		margin-bottom: 1.25em;
	}

	.tabLink
	{
		padding: 8px 18px;
		font-size: 17px;
		color: #333333;
		text-decoration: none;
		border-bottom: 2px solid transparent;
		margin-bottom: -2px;
	}

		.tabLink:hover
		{
			background-color: #EEEEEE;
		}

		.tabLink.activeTab
		{
			font-weight: bold;
			color: var(--primary-color-text);
			border-bottom-color: var(--primary-color);
		}

	/* Cards (visually consistent with .outerContainer on the Release tab) */
	.card
	{
		border: 1px solid #DDDDDD;
		padding: 12px;
		background-color: #FFFFFF;
		margin-bottom: 1.5em;
		box-shadow: 2px 2px 2px rgba(0,0,0,0.4);
	}

	.errorBox
	{
		background-color: #FFE0E0;
		border: 1px solid #C00000;
		color: #660000;
		padding: 8px 12px;
		margin-bottom: 1em;
		white-space: pre-wrap;
		word-break: break-word;
	}

		.errorBox.dialogError
		{
			margin: 12px 0 0 0;
		}

	.loadingRow
	{
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 8px;
	}

	.hintText
	{
		color: #555555;
		font-size: 0.92em;
		margin: 0 0 10px 0;
	}

	.mutedText
	{
		color: #767676;
	}

	.emptyMessage
	{
		color: #666666;
		font-style: italic;
		margin: 8px 0;
	}

	/* Toolbar */
	.toolbarCard
	{
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		align-items: center;
	}

	.filterInput
	{
		flex: 1 1 200px;
		min-width: 140px;
		max-width: 400px;
		padding: 7px 10px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		font-size: 15px;
		box-sizing: border-box;
	}

	button.gradientButton:disabled
	{
		opacity: 0.55;
		pointer-events: none;
	}

	/* Secrets list */
	.listMeta
	{
		color: #666666;
		font-size: 0.85em;
		margin-bottom: 6px;
	}

	.listHeader,
	.secretRow
	{
		display: grid;
		grid-template-columns: minmax(0, 1fr) 150px 190px;
		gap: 4px 12px;
		align-items: start;
	}

	.listHeader
	{
		font-weight: bold;
		color: #555555;
		font-size: 0.8em;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		padding: 4px 6px;
		border-bottom: 2px solid #DDDDDD;
	}

	.secretRow
	{
		padding: 10px 6px;
		border-bottom: 1px solid #E4E4E4;
	}

		.secretRow:hover
		{
			background-color: #F5FAFF;
		}

	.secretMain
	{
		min-width: 0;
	}

	.secretTitleRow
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 4px 8px;
	}

	.secretNameBtn
	{
		background: none;
		border: none;
		padding: 0;
		color: #0066CC;
		font-weight: bold;
		font-size: 1.05em;
		cursor: pointer;
		text-align: left;
		word-break: break-all;
	}

		.secretNameBtn:hover,
		.secretNameBtn:focus
		{
			text-decoration: underline;
		}

	.scopeBadge,
	.writeOnlyBadge,
	.warningChip
	{
		display: inline-block;
		padding: 1px 8px;
		border-radius: 9px;
		font-size: 0.8em;
		white-space: nowrap;
		cursor: help;
	}

	.scopeBadge
	{
		background-color: #E3EEFA;
		color: #1F4E79;
		border: 1px solid #A9C6E6;
	}

		.scopeBadge.scopeOrchestratorOnly
		{
			background-color: #EEE8F7;
			color: #4B2C7A;
			border-color: #C2B0E0;
		}

		.scopeBadge.scopeSelectedAgents
		{
			background-color: #E6F4F1;
			color: #1E5E52;
			border-color: #9FD0C5;
		}

	.writeOnlyBadge
	{
		background-color: #444444;
		color: #FFFFFF;
		border: 1px solid #444444;
	}

	.secretDescription
	{
		margin-top: 3px;
		color: #444444;
		font-size: 0.9em;
		white-space: pre-line;
		word-break: break-word;
	}

	.secretValueRow
	{
		margin-top: 4px;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px 8px;
		min-width: 0;
	}

	.fingerprint
	{
		font-family: Consolas, monospace;
		font-size: 0.9em;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 1px 6px;
		cursor: help;
	}

	.blankValue
	{
		font-style: italic;
		color: #666666;
		font-size: 0.9em;
	}

	.revealedRow
	{
		margin-top: 4px;
		display: flex;
		align-items: flex-start;
		gap: 8px;
		min-width: 0;
	}

	.secretValue
	{
		min-width: 0;
		font-family: Consolas, monospace;
		font-size: 0.9em;
		background-color: #FFF8DC;
		border: 1px solid #E0D090;
		border-radius: 3px;
		padding: 2px 6px;
		word-break: break-all;
		white-space: pre-wrap;
		max-height: 8em;
		overflow: auto;
	}

	.revealedHint
	{
		flex: 0 0 auto;
		font-size: 0.8em;
	}

	.noteText
	{
		margin-top: 3px;
		color: #555555;
		font-size: 0.85em;
	}

	.pendingNote
	{
		cursor: help;
	}

	.warningRow
	{
		margin-top: 4px;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px 8px;
	}

	.warningChip
	{
		background-color: #FFEFC9;
		color: #6B4700;
		border: 1px solid #E0B93F;
	}

	.copyBtn
	{
		flex: 0 0 auto;
		font-size: 0.8em;
		padding: 2px 8px;
		border: 1px solid #999999;
		border-radius: 3px;
		background-color: #FAFAFA;
		cursor: pointer;
	}

		.copyBtn:hover
		{
			background-color: #FFFFFF;
			border-color: var(--primary-color);
		}

		.copyBtn:disabled
		{
			opacity: 0.55;
			cursor: default;
		}

	.syncBadge
	{
		display: inline-block;
		padding: 2px 10px;
		border-radius: 10px;
		font-weight: bold;
		font-size: 0.9em;
		white-space: nowrap;
		cursor: help;
	}

	.syncAll
	{
		background-color: #D4F5D4;
		color: #0A5A0A;
		border: 1px solid #77CC77;
	}

	.syncPartial
	{
		background-color: #FFEFC9;
		color: #7A5200;
		border: 1px solid #E0B93F;
	}

	.syncNone
	{
		background-color: #FFDBDB;
		color: #8B0000;
		border: 1px solid #E08585;
	}

	.syncNoAgents
	{
		background-color: #EEEEEE;
		color: #555555;
		border: 1px solid #CCCCCC;
	}

	.lastEditCell
	{
		color: #555555;
		font-size: 0.9em;
		white-space: pre-line;
	}

	.downloadHint
	{
		margin-bottom: 10px;
	}

	a.downloadLink
	{
		max-width: none;
	}

	.downloadResult
	{
		margin-top: 10px;
		color: #444444;
		font-size: 0.92em;
	}

		.downloadResult:empty
		{
			display: none;
		}

	.excludedKey
	{
		margin: 0 4px;
	}

	/* Agent Authorization view */
	.agentAuthRow
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;
		gap: 10px 16px;
		padding: 12px 6px;
		border-bottom: 1px solid #E4E4E4;
	}

	.agentAuthInfo
	{
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		min-width: 0;
	}

	.agentAuthName
	{
		font-weight: bold;
		font-size: 1.05em;
		word-break: break-all;
	}

	.agentAuthState
	{
		font-size: 0.85em;
		padding: 1px 8px;
		border-radius: 9px;
		background-color: #EEEEEE;
		color: #666666;
		white-space: nowrap;
	}

		.agentAuthState.stateAuthorized
		{
			background-color: #D4F5D4;
			color: #0A5A0A;
		}

	.statusDot
	{
		flex: 0 0 auto;
		display: inline-block;
		width: 10px;
		height: 10px;
		border-radius: 50%;
	}

	.dotGreen
	{
		background-color: #2E9E2E;
	}

	.dotGray
	{
		background-color: #AAAAAA;
	}

	.dotRed
	{
		background-color: #CC3333;
	}

	/* Dialogs */
	.secretDialogOverlay
	{
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0,0,0,0.45);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
		padding: 12px;
		box-sizing: border-box;
	}

	.secretDialog
	{
		background-color: #FFFFFF;
		padding: 18px 20px;
		width: 100%;
		max-width: 600px;
		max-height: calc(100vh - 24px);
		overflow-y: auto;
		border-radius: 6px;
		box-shadow: 0 4px 24px rgba(0,0,0,0.35);
		box-sizing: border-box;
	}

	.secretDialogTitle
	{
		font-size: 1.35em;
		font-weight: bold;
		margin-bottom: 8px;
	}

		.secretDialogTitle:focus
		{
			outline: none;
		}

	.fieldLabel
	{
		display: block;
		font-weight: bold;
		font-size: 0.9em;
		color: #444444;
		margin: 10px 0 3px 0;
	}

	.fieldInput
	{
		display: block;
		width: 100%;
		box-sizing: border-box;
		padding: 7px 9px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		font-size: 15px;
	}

	.descriptionInput
	{
		resize: vertical;
		font-family: inherit;
	}

	.valueInput
	{
		font-family: Consolas, monospace;
		resize: vertical;
		min-height: 90px;
	}

	.fieldGroup
	{
		border: none;
		margin: 0;
		padding: 0;
		min-width: 0;
	}

		/* A legend's top margin is not applied inside a fieldset, so the fieldset carries the label's 10px top margin instead. */
		fieldset.fieldGroup
		{
			margin-top: 10px;
		}

		fieldset.fieldGroup legend.fieldLabel
		{
			padding: 0;
			margin-top: 0;
		}

	.radioRow,
	.checkRow
	{
		display: block;
		margin: 4px 0;
		cursor: pointer;
	}

	.checkRow:has(input:disabled)
	{
		cursor: default;
	}

	.checkRow
	{
		margin-top: 12px;
		font-weight: bold;
		font-size: 0.9em;
		color: #444444;
	}

	.fieldHint
	{
		margin: 2px 0 0 0;
		font-size: 0.85em;
	}

	.scopeAgentList
	{
		margin: 6px 0 0 22px;
		padding: 6px 8px;
		border: 1px solid #DDDDDD;
		border-radius: 4px;
		max-height: 220px;
		overflow-y: auto;
	}

	.scopeAgentItem
	{
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 3px 0;
		cursor: pointer;
	}

	.scopeAgentText
	{
		min-width: 0;
		word-break: break-word;
	}

	.scopeAgentName
	{
		font-weight: bold;
	}

	.notAuthorizedText
	{
		color: #7A5200;
	}

	.currentValueRow
	{
		font-size: 0.9em;
		color: #444444;
	}

	.generateRow
	{
		margin-top: 6px;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px 8px;
	}

		.generateRow .generateBtn
		{
			font-size: 0.9em;
			padding: 4px 10px;
		}

	.generateSizeLabel
	{
		font-size: 0.9em;
		color: #444444;
	}

	.generateSizeSelect
	{
		font-size: 0.9em;
		padding: 3px 4px;
		border: 1px solid #AAAAAA;
		border-radius: 3px;
		max-width: 100%;
	}

	.generateHint
	{
		margin: 4px 0 0 0;
		font-size: 0.85em;
	}

	.metadataSection
	{
		margin-top: 14px;
		border-top: 1px solid #E0E0E0;
		padding-top: 10px;
		font-size: 0.9em;
		color: #444444;
	}

	.metaRow
	{
		margin: 3px 0;
	}

	.metaLabel
	{
		display: inline-block;
		min-width: 72px;
		font-weight: bold;
		color: #666666;
	}

	.metaBlock
	{
		margin-top: 8px;
	}

	.metaSubheading
	{
		font-weight: bold;
		color: #666666;
	}

	.agentSyncList
	{
		margin-top: 8px;
	}

	.agentSyncItem
	{
		display: flex;
		align-items: center;
		gap: 6px;
		margin: 3px 0;
	}

	.syncedText
	{
		color: #0A5A0A;
	}

	.notSyncedText
	{
		color: #8B0000;
	}

	.leavingHeading
	{
		font-weight: bold;
		margin-bottom: 6px;
	}

		.leavingHeading:focus
		{
			outline: none;
		}

	.leavingList
	{
		margin: 0 0 10px 0;
		padding-left: 22px;
	}

	.leavingQuestion
	{
		margin: 0;
	}

	.secretDialogButtons
	{
		margin-top: 18px;
		display: flex;
		align-items: center;
	}

		.secretDialogButtons.wrapButtons
		{
			flex-wrap: wrap;
			gap: 6px 0;
		}

	.deleteBtn
	{
		margin-left: auto;
		margin-right: 0;
	}

	/* Narrow viewports */
	@media (max-width: 760px)
	{
		.listHeader
		{
			display: none;
		}

		.listHeader,
		.secretRow
		{
			grid-template-columns: minmax(0, 1fr) auto;
		}

		/* The "Last edit" column moves down below the secret and sync information. */
		.lastEditCell
		{
			grid-column: 1 / -1;
			font-size: 0.85em;
		}
	}

	@media (max-width: 380px)
	{
		.card
		{
			padding: 8px;
		}

		.pageHeading
		{
			font-size: 26px;
		}
	}
</style>
